import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { scaled } from '../styles/theme';
import { keyframes } from '@emotion/react';
import { fetchLatestAnnouncements } from '../utils/api';
import { formatDate } from '../utils/formatters';
import type { Announcement, FilterState } from '../types';
import { useT } from '../i18n';

// Slim live-ticker band, one text line tall, between the filter panel and the
// charts inside DashboardGrid (grid gap supplies the vertical rhythm, so no
// own margins). Deliberately NOT a tile: a flat strip of the dz-shared-styles
// yellow (--color-events) with no shadow, so it reads as a band rather than
// another card among cards.
const Band = styled.div`
  display: flex;
  align-items: center;
  background: var(--color-events);
  border-radius: var(--radius-s);
  padding: var(--space-2) 0;
`;

// Hairlines sit on yellow here, so the grey token the tiles use
// (--color-bg-muted) would wash out — use a translucent mix of the text
// color instead.
const onYellowHairline = 'color-mix(in srgb, var(--color-text-default) 30%, transparent)';

// Inline caption at the left edge of the band. The divider gives ticker items
// a clean edge to disappear under.
// h2: the ticker is a top-level region of the widget, under the host page's h1.
// The whole rule sits inside `&&` (doubled class, specificity 0,2,0) so a host
// theme's `h2` rule can't take it over — see CardTitle in styles/components.tsx.
// The media query has to be INSIDE the wrapper too: at plain specificity its
// padding override would lose to the base declaration above it.
const BandLabel = styled.h2`
  && {
    /* dz-shared-styles sets text-align: center on bare h2; the && wrapper only
       beats it for properties declared here. The padding below covers the
       theme's own h2 padding for the same reason. */
    text-align: left;
    flex: 0 0 auto;
    font-family: var(--sans-stack);
    font-size: ${scaled(13)};
    font-weight: 600;
    line-height: 1.2;
    color: var(--color-text-default);
    margin: 0;
    padding: 0 var(--space-4);
    border-right: 1px solid ${onYellowHairline};

    /* The full caption would eat over half a phone screen's width — swap to the
       short form and leave the room to the ticker. Both spans stay in the DOM
       (screen readers always get the full one, the short one is aria-hidden). */
    .label-short {
      display: none;
    }
    @media (max-width: 600px) {
      padding: 0 var(--space-3);
      /* Visually hidden, not display:none — the heading keeps its full text in
         the accessibility tree on all viewports. */
      .label-full {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
      }
      .label-short {
        display: inline;
      }
    }
  }
`;

// The track holds the item list twice; sliding it left by exactly one copy's
// width (-50% of the track) and looping lands on an identical frame — a
// seamless infinite ticker.
const tickerScroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

// When the ticker runs, the viewport clips; when it doesn't (content fits, or
// the user prefers reduced motion), it falls back to plain manual horizontal
// scrolling with a hidden scrollbar, like before.
const Viewport = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  scroll-snap-type: x proximity;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &[data-animate='true'] {
    overflow: hidden;
  }
`;

// Duration is set inline per content width so the speed stays constant no
// matter how many/how long the headlines are; the 60s here is a dead fallback.
// Pausing on hover AND focus-within keeps items clickable and tabbable while
// the ticker runs.
const Track = styled.div`
  display: flex;
  width: max-content;

  [data-animate='true'] > & {
    animation: ${tickerScroll} 60s linear infinite;
  }
  [data-animate='true']:hover > &,
  [data-animate='true']:focus-within > & {
    animation-play-state: paused;
  }
`;

const Group = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
`;

// Every item keeps its divider (no last-of-type reset): the divider doubles
// as the seam between the two copies of the loop, so dropping the last one
// would make the wrap-around point visible.
const NewsItem = styled.li`
  flex: 0 0 auto;
  display: flex;
  scroll-snap-align: start;
  border-right: 1px solid ${onYellowHairline};
`;

const NewsRow = styled.button`
  padding: var(--space-1) var(--space-3);
  background: none;
  border: 0;
  border-radius: var(--radius-s);
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: background-color 0.15s ease;

  &:hover {
    /* Translucent darkening so the hover tint composites over the yellow. */
    background: color-mix(in srgb, var(--color-text-default) 8%, transparent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

// One uncut line per item: "<date> <company>: <headline>".
const Line = styled.span`
  font-family: var(--sans-stack);
  font-size: ${scaled(14)};
  font-weight: 400;
  line-height: 1.3;
  color: var(--color-text-default);
  white-space: nowrap;

  time {
    font-weight: 700;
  }
`;

// Ticker speed in px/s — brisk enough to read as "live", slow enough to read.
const TICKER_SPEED = 40;

interface LatestNewsProps {
  /** Active filter state — the band shows the newest matches. */
  filters: FilterState;
  /** Cluster-id narrowing from map/chart selections (country, Wahlkreis,
      aggregate bubble) — same values the tables receive. */
  clusterIds?: number[] | null;
  /** Industry-drill narrowing (`L<level>:<label>`) — same as the tables. */
  industriesNarrow?: string[];
  onItemClick: (announcement: Announcement) => void;
}

export function LatestNews({ filters, clusterIds, industriesNarrow, onItemClick }: LatestNewsProps) {
  const m = useT();
  // Items come from GET /api/announcements/latest, refetched whenever the
  // filters or chart-side narrowings change. The previous list stays visible
  // while the new one loads (no flicker); when nothing matches (or on error)
  // the band renders nothing — an empty strip would look broken.
  const [items, setItems] = useState<Announcement[]>([]);
  // Depend on the filters' VALUES, not on the objects' identity — the same
  // shape useChartData and useTablePage use. App rebuilds `effectiveFilters`
  // (and these arrays) whenever anything upstream changes, including when
  // /api/meta lands and only the date bounds move; an identity dependency
  // refetched the band on every one of those, which is one of the duplicate
  // requests the first page load used to make.
  const filtersKey = [
    filters.dateFrom,
    filters.dateTo,
    filters.federalStates.join(','),
    filters.investmentTypes.join(','),
    filters.investmentStatuses.join(','),
    filters.industries.join(','),
    filters.companies.join(','),
    filters.search,
    filters.spotlight,
    clusterIds ? clusterIds.join(',') : '',
    industriesNarrow ? industriesNarrow.join(',') : '',
  ].join('|');
  // The effect reads the live objects; only the key decides when it reruns.
  // Fields are assigned in place rather than replacing the object, so a render
  // that changes nothing here allocates nothing.
  const latestArgsRef = useRef<Pick<LatestNewsProps, 'filters' | 'clusterIds' | 'industriesNarrow'>>({
    filters, clusterIds, industriesNarrow,
  });
  latestArgsRef.current.filters = filters;
  latestArgsRef.current.clusterIds = clusterIds;
  latestArgsRef.current.industriesNarrow = industriesNarrow;
  useEffect(() => {
    const controller = new AbortController();
    const { filters: f, clusterIds: ids, industriesNarrow: narrow } = latestArgsRef.current;
    fetchLatestAnnouncements(f, controller.signal, ids ?? undefined, narrow)
      .then(data => setItems(data))
      .catch(() => {}); // aborted or failed — keep whatever is shown
    return () => controller.abort();
  }, [filtersKey]);

  // OS-level "reduce motion" wins over the ticker — those users get the
  // static, manually scrollable band instead.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Animate only when one copy of the items actually overflows the viewport —
  // a ticker that fits entirely would just wander pointlessly. Duration is
  // derived from the measured copy width so speed is constant. ResizeObserver
  // catches container resizes and late font loading.
  const viewportRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLOListElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [duration, setDuration] = useState(0);
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const group = groupRef.current;
    if (!viewport || !group) return;
    const measure = () => {
      const width = group.scrollWidth;
      setOverflows(width > viewport.clientWidth);
      setDuration(width / TICKER_SPEED);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(group);
    return () => ro.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  const animate = overflows && !reducedMotion;

  const renderItems = (hidden: boolean) =>
    items.map(a => (
      <NewsItem key={a.id}>
        <NewsRow type="button" tabIndex={hidden ? -1 : undefined} onClick={() => onItemClick(a)}>
          <Line>
            <time dateTime={a.announced_event_date ?? undefined}>
              {formatDate(a.announced_event_date)}
            </time>{' '}
            {/* Headline only. Most headlines name the company themselves (14
                of the 20 newest on prod), so the prefix read as a duplicate;
                the company is on the card the item opens either way. */}
            {a.headline}
          </Line>
        </NewsRow>
      </NewsItem>
    ));

  return (
    <Band>
      <BandLabel>
        <span className="label-full">{m.news.label}</span>
        <span className="label-short" aria-hidden="true">
          {m.news.labelShort}
        </span>
      </BandLabel>
      <Viewport ref={viewportRef} data-animate={animate ? 'true' : 'false'}>
        <Track style={{ animationDuration: `${duration}s` }}>
          <Group ref={groupRef}>{renderItems(false)}</Group>
          {/* Second copy makes the loop seamless; hidden from the a11y tree
              and tab order, but still clickable mid-seam. */}
          {animate && <Group aria-hidden="true">{renderItems(true)}</Group>}
        </Track>
      </Viewport>
    </Band>
  );
}
